import type { Rating } from './types'

export const MAX_TAG_LENGTH = 100
export const MAX_TAGS = 200

/**
 * Normalises a tag the same way the backend does: lowercase, trimmed, inner
 * whitespace as underscores. Returns '' for input that is not a usable tag.
 */
export function normalizeTag(raw: string): string {
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return ''
  const tag = raw.trim().toLowerCase().replace(/\s+/g, '_')
  return tag.length > MAX_TAG_LENGTH ? '' : tag
}

/** Splits free text (commas or newlines) into normalised, de-duplicated tags. */
export function parseTags(input: string): string[] {
  return uniqueTags(input.split(/[,\n]/).map(normalizeTag))
}

export function uniqueTags(tags: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const tag of tags) {
    if (!tag || seen.has(tag)) continue
    seen.add(tag)
    out.push(tag)
    if (out.length >= MAX_TAGS) break
  }
  return out
}

/**
 * Tag columns are JSON in the database. Most rows hold string arrays, but be
 * lenient about nulls and the occasional object (`{ name }` / `{ tag }`).
 */
export function toTagList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const item of value) {
    if (typeof item === 'string') out.push(item)
    else if (item && typeof item === 'object') {
      const named = (item as Record<string, unknown>).name ?? (item as Record<string, unknown>).tag
      if (typeof named === 'string') out.push(named)
    }
  }
  return out.filter(Boolean)
}

/** Display form of a tag: underscores as spaces. */
export function tagLabel(tag: string): string {
  return tag.replace(/_/g, ' ')
}

/** Search queries are space separated; the suggestion box completes the last word. */
export function lastToken(query: string): string {
  const parts = query.split(/\s+/)
  return parts[parts.length - 1] ?? ''
}

export function replaceLastToken(query: string, token: string): string {
  const parts = query.trimEnd().split(/\s+/).filter(Boolean)
  if (query.endsWith(' ') || parts.length === 0) parts.push(token)
  else parts[parts.length - 1] = token
  return parts.join(' ') + ' '
}

export const RATINGS: { value: Rating; label: string; short: string; description: string }[] = [
  { value: 's', label: 'Safe', short: 'S', description: 'Fine for everyone' },
  { value: 'q', label: 'Questionable', short: 'Q', description: 'Suggestive, not explicit' },
  { value: 'e', label: 'Explicit', short: 'E', description: 'Adults only' },
]

export function ratingInfo(rating: string | undefined) {
  return RATINGS.find((r) => r.value === rating) ?? { value: rating ?? '?', label: 'Unrated', short: '?', description: '' }
}

export function isRating(value: unknown): value is Rating {
  return value === 's' || value === 'q' || value === 'e'
}

/** Only http(s) links may be rendered as anchors. */
export function safeExternalUrl(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}
